import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import {
  Container, Typography, Box, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Stack, Fade, Slide, Chip, IconButton, Tooltip, Card, CardContent
} from '@mui/material';
import {
  LockOpen as UnlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  WifiOff as ConnectionErrorIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import { useWallet } from '../context/WalletContext';
import { NETWORKS, fetchUserLocks, fetchTokenSymbol, fetchTokenDecimals } from '../utils/contractUtils';
import { themeCustom, GradientPaper, HeaderBar } from '../utils/themeUtils';

export default function UnlockedTokens() {
  const {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
    connectWallet,
    disconnectWallet,
    isConnecting,
    connectionStatus,
    isInitialized,
    initError
  } = useWallet();

  const [unlockedLocks, setUnlockedLocks] = useState([]);
  const [fetchingLocks, setFetchingLocks] = useState(false);
  const [alert, setAlert] = useState({ type: 'info', message: '' });
  const [showAlert, setShowAlert] = useState(false);
  const [lockTokenSymbolMap, setLockTokenSymbolMap] = useState({});
  const [lockTokenDecimalsMap, setLockTokenDecimalsMap] = useState({});
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check for pre-loaded data from Dashboard
  useEffect(() => {
    const preloadedData = sessionStorage.getItem('dashboardUnlockedTokens');
    if (preloadedData && isConnected && address) {
      try {
        const parsedData = JSON.parse(preloadedData);
        // Only use pre-loaded data if it's for the same address
        if (parsedData.length > 0 && parsedData[0].owner?.toLowerCase() === address.toLowerCase()) {
          console.log('Using pre-loaded unlocked tokens data');
          setUnlockedLocks(parsedData);
          setIsInitialLoad(false);
          // Clear the pre-loaded data after using it
          sessionStorage.removeItem('dashboardUnlockedTokens');
          return;
        }
      } catch (error) {
        console.error('Error parsing pre-loaded data:', error);
      }
    }
    
    // If no pre-loaded data, fetch normally
    if (isConnected && contract && provider) {
      fetchUnlockedLocks(true);
    }
  }, [isConnected, contract, provider, address]);

  // Enhanced fetch unlocked locks with better error handling
  const fetchUnlockedLocks = useCallback(async (forceRefresh = false) => {
    console.log('=== fetchUnlockedLocks called ===');
    console.log('isConnected:', isConnected);
    console.log('contract:', contract);
    console.log('provider:', provider);
    console.log('address:', address);
    
    if (!isConnected || !contract || !provider) {
      console.log('Not connected or contract not available');
      return;
    }
    
    setFetchingLocks(true);
    setIsRefreshing(true);
    
    try {
      console.log('Fetching unlocked locks...');
      
      // Get total number of locks
      console.log('Calling contract.lockCount()...');
      const lockCount = await contract.lockCount();
      console.log(`Total locks: ${lockCount.toString()}`);
      
      if (lockCount.toString() === '0') {
        setUnlockedLocks([]);
        setFetchingLocks(false);
        setIsRefreshing(false);
        setIsInitialLoad(false);
        return;
      }
      
      // Only fetch recent locks for better performance (last 100)
      const maxLocksToFetch = 100;
      const startIndex = Math.max(0, lockCount - maxLocksToFetch);
      const locksToFetch = Math.min(lockCount, maxLocksToFetch);
      
      console.log(`Fetching recent locks from ${startIndex} to ${startIndex + locksToFetch - 1}`);
      
      // Fetch locks in batches for better performance
      const batchSize = 20;
      const allLocks = [];
      
      for (let batchStart = startIndex; batchStart < startIndex + locksToFetch; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, startIndex + locksToFetch);
        const batchPromises = [];
        
        for (let i = batchStart; i < batchEnd; i++) {
          batchPromises.push(contract.locks(i));
        }
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach((lock, index) => {
          allLocks.push({ ...lock, lockId: batchStart + index });
        });
      }
      
      console.log(`Fetched ${allLocks.length} locks`);
      
      // Filter ONLY user's withdrawn locks
      const userUnlockedLocks = allLocks.filter(lock => 
        lock.owner.toLowerCase() === address.toLowerCase() && lock.withdrawn
      );
      
      console.log(`Found ${userUnlockedLocks.length} unlocked locks for user`);
      
      if (userUnlockedLocks.length === 0) {
        setUnlockedLocks([]);
        setFetchingLocks(false);
        setIsRefreshing(false);
        setIsInitialLoad(false);
        return;
      }
      
      // Get unique token addresses for this user only
      const uniqueTokenAddresses = [...new Set(userUnlockedLocks.map(lock => lock.token))];
      console.log(`Unique tokens for user: ${uniqueTokenAddresses.length}`);
      
      // Fetch token details in parallel with caching
      const tokenDetailsPromises = uniqueTokenAddresses.map(async (tokenAddress) => {
        try {
          const tokenContract = new ethers.Contract(
            tokenAddress, 
            ['function symbol() view returns (string)', 'function decimals() view returns (uint8)'], 
            provider
          );
          
          const [symbol, decimals] = await Promise.all([
            tokenContract.symbol(),
            tokenContract.decimals()
          ]);
          
          return { tokenAddress, symbol, decimals };
        } catch (error) {
          console.error(`Error fetching token details for ${tokenAddress}:`, error);
          return { 
            tokenAddress, 
            symbol: tokenAddress.slice(0, 6) + '...' + tokenAddress.slice(-4), 
            decimals: 18 
          };
        }
      });
      
      const tokenDetailsResults = await Promise.all(tokenDetailsPromises);
      
      // Create token details map
      const tokenDetailsMap = {};
      tokenDetailsResults.forEach(({ tokenAddress, symbol, decimals }) => {
        tokenDetailsMap[tokenAddress] = { symbol, decimals };
      });
      
      // Process user's unlocked locks
      const locksWithDetails = userUnlockedLocks.map(lock => {
        const tokenDetails = tokenDetailsMap[lock.token];
        return {
          ...lock,
          symbol: tokenDetails.symbol,
          decimals: tokenDetails.decimals,
          formattedAmount: ethers.utils.formatUnits(lock.amount, tokenDetails.decimals)
        };
      });
      
      console.log('Final unlocked locks with details:', locksWithDetails);
      setUnlockedLocks(locksWithDetails);
      setLastFetchTime(Date.now());
      setIsInitialLoad(false);
      console.log('Unlocked locks fetched successfully');
      
    } catch (error) {
      console.error('Error fetching unlocked locks:', error);
      setAlert({ 
        type: 'error', 
        message: error.message || `Failed to fetch unlocked tokens for ${networkConfig.name}. Please try again later.` 
      });
      setShowAlert(true);
      setIsInitialLoad(false);
    } finally {
      setFetchingLocks(false);
      setIsRefreshing(false);
    }
  }, [isConnected, contract, provider, address, networkConfig]);

  // Clear state when account changes
  useEffect(() => {
    setUnlockedLocks([]);
    setLastFetchTime(0);
    setIsInitialLoad(true);
  }, [address]);

  // Alert animation
  useEffect(() => {
    if (alert.message) setShowAlert(true);
    if (alert.message && alert.type === 'success') {
      const timer = setTimeout(() => setShowAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Manual refresh function
  const handleRefresh = () => {
    fetchUnlockedLocks(true);
  };

  const formatUnlockTime = (unlockTime) => {
    const date = new Date(unlockTime * 1000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Render wallet selector
  const renderWalletSelector = () => {
    if (wallets.length <= 1) return null;
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Select Wallet:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {wallets.map((wallet) => (
            <Chip
              key={wallet.address}
              label={`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`}
              color={activeWallet === wallet.address ? 'primary' : 'default'}
              onClick={() => switchWallet(wallet.address)}
              clickable
            />
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 50%, #1a1a2e 100%)',
        py: isMobile ? 2 : 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 113, 235, 0.1) 0%, transparent 50%), 
            radial-gradient(circle at 80% 20%, rgba(0, 86, 179, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0.5 : 2, position: 'relative', zIndex: 1 }}>
          <HeaderBar>
            <UnlockIcon sx={{ 
              fontSize: isMobile ? 32 : 64, 
              mr: isMobile ? 1 : 2,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
            }} />
            <Typography 
              variant={isMobile ? 'h6' : 'h4'} 
              fontWeight={700} 
              letterSpacing={1} 
              sx={{ 
                color: '#fff', 
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #ffffff 0%, #f5f5f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Unlocked Tokens History
            </Typography>
          </HeaderBar>

          <Stack spacing={isMobile ? 2 : 3}>
            {showAlert && alert && (
              <Fade in={showAlert} timeout={400} unmountOnExit>
                <Box mb={2} width="100%">
                  <Alert severity={alert.type || 'info'}>{alert.message}</Alert>
                </Box>
              </Fade>
            )}

            {/* Network Selection */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Select Network
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="#B3B3B3" gutterBottom>
                  Current Network: {NETWORKS[currentNetwork]?.name || 'Unknown'}
                </Typography>
              </Box>
            </GradientPaper>

            {/* Wallet Connection Status */}
            {!isConnected && (
              <GradientPaper>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ color: '#0071EB', fontWeight: 700 }}>
                    Connect Your Wallet
                  </Typography>
                  <Typography variant="body2" color="#B3B3B3" sx={{ mb: 3 }}>
                    Please connect your wallet using the button in the navigation bar to view your unlocked tokens history.
                  </Typography>
                </Box>
              </GradientPaper>
            )}

            {/* Unlocked Tokens Table */}
            {isConnected && (
              <GradientPaper>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  mb: 3,
                  gap: isMobile ? 1 : 0
                }}>
                  <Typography 
                    variant={isMobile ? 'body1' : 'h6'} 
                    sx={{ 
                      color: '#0071EB',
                      fontWeight: 700
                    }}
                  >
                    Your Unlocked Tokens History
                  </Typography>
                  <Tooltip title="Refresh">
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={fetchingLocks}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{ color: '#0071EB' }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(0, 113, 235, 0.1)', border: '1px solid rgba(0, 113, 235, 0.3)' }}>
                  <Typography variant="body2" color="#0071EB" gutterBottom fontWeight={600}>
                    Unlocked Tokens History:
                  </Typography>
                  <Typography variant="body2" color="#B3B3B3">
                    • Shows all tokens you have successfully unlocked and withdrawn
                  </Typography>
                  <Typography variant="body2" color="#B3B3B3">
                    • This is a historical record of your past token unlocks
                  </Typography>
                </Box>
                
                {isInitialLoad ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: '#0071EB', mb: 2 }} />
                    <Typography color="#B3B3B3" variant={isMobile ? 'body2' : 'body1'}>
                      Loading your unlocked tokens history...
                    </Typography>
                  </Box>
                ) : fetchingLocks ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: '#0071EB' }} />
                    <Typography color="#B3B3B3" sx={{ mt: 1 }} variant={isMobile ? 'body2' : 'body1'}>
                      Refreshing...
                    </Typography>
                  </Box>
                ) : unlockedLocks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="#B3B3B3" variant={isMobile ? 'body2' : 'body1'}>
                      No unlocked tokens found for this account on {networkConfig.name}.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflow: 'auto' }}>
                    {isMobile ? (
                      // Mobile card layout
                      <Stack spacing={2}>
                        {unlockedLocks.map(lock => (
                          <Card key={lock.lockId} sx={{ 
                            background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                          }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" sx={{ color: '#0071EB', fontWeight: 700 }}>
                                  {lock.symbol}
                                </Typography>
                                <Chip
                                  label="Unlocked"
                                  color="success"
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                />
                              </Box>
                              <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
                                Amount: {lock.formattedAmount}
                              </Typography>
                              <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
                                ID: {lock.lockId}
                              </Typography>
                              <Typography variant="body2" color="#B3B3B3">
                                Unlocked: {formatUnlockTime(lock.unlockTime)}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      // Desktop table layout
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          mb: 3,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                          background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <Table size="medium">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, color: '#0071EB' }}>ID</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#0071EB' }}>Token</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#0071EB' }}>Amount</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#0071EB' }}>Unlock Time</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#0071EB' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {unlockedLocks.map(lock => (
                              <Slide 
                                key={lock.lockId} 
                                direction="up" 
                                in 
                                mountOnEnter 
                                unmountOnExit
                              >
                                <TableRow hover>
                                  <TableCell>{lock.lockId}</TableCell>
                                  <TableCell>{lock.symbol}</TableCell>
                                  <TableCell>{lock.formattedAmount}</TableCell>
                                  <TableCell>{formatUnlockTime(lock.unlockTime)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label="Unlocked"
                                      color="success"
                                      size="small"
                                      icon={<CheckCircleIcon />}
                                    />
                                  </TableCell>
                                </TableRow>
                              </Slide>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </GradientPaper>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
} 